import { addDays } from "date-fns/addDays";
import { addMonths } from "date-fns/addMonths";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

import { useUiStore } from "@stores/uiStore";

/**
 *
 * @param date
 * @param freeze It is recommended to set it to `true`.
 * @returns
 */
export function convertToAPIDate(
  date: string | Date,
  freeze?: boolean,
): string {
  if (validateDate(date)) {
    const dateInstance = new Date(date);
    const year = dateInstance.getFullYear();
    const month = convertToTwoDigits(dateInstance.getMonth() + 1);
    const day = convertToTwoDigits(dateInstance.getDate());

    const utcYear = dateInstance.getUTCFullYear();
    const utcMonth = convertToTwoDigits(dateInstance.getUTCMonth() + 1);
    const utcDay = convertToTwoDigits(dateInstance.getUTCDate());

    return freeze
      ? `${year}/${month}/${day}`
      : `${utcYear}/${utcMonth}/${utcDay}`;
  }
  return "";
}
/**
 *
 * @param date
 * @param freeze It is recommended to set it to `false`.
 * @returns
 */
export function convertToFullAPIDate(
  date: string | Date,
  freeze?: boolean,
): string {
  if (validateDate(date)) {
    const dateInstance = new Date(date);
    const year = dateInstance.getFullYear();
    const month = convertToTwoDigits(dateInstance.getMonth() + 1);
    const day = convertToTwoDigits(dateInstance.getDate());
    const hour = convertToTwoDigits(dateInstance.getHours());
    const minute = convertToTwoDigits(dateInstance.getMinutes());
    const second = convertToTwoDigits(dateInstance.getSeconds());

    const utcYear = dateInstance.getUTCFullYear();
    const utcMonth = convertToTwoDigits(dateInstance.getUTCMonth() + 1);
    const utcDay = convertToTwoDigits(dateInstance.getUTCDate());
    const utcHour = convertToTwoDigits(dateInstance.getUTCHours());
    const utcMinute = convertToTwoDigits(dateInstance.getUTCMinutes());
    const utcSecond = convertToTwoDigits(dateInstance.getUTCSeconds());

    return freeze
      ? `${year}/${month}/${day} - ${hour}:${minute}:${second}`
      : `${utcYear}/${utcMonth}/${utcDay} - ${utcHour}:${utcMinute}:${utcSecond}`;
  }
  return "";
}

export function convertToTime(date: string | Date, freeze?: boolean): string {
  if (validateDate(date)) {
    const dateInstance = new Date(date);
    const hour = convertToTwoDigits(dateInstance.getHours());
    const minute = convertToTwoDigits(dateInstance.getMinutes());

    const utcHour = convertToTwoDigits(dateInstance.getUTCHours());
    const utcMinute = convertToTwoDigits(dateInstance.getUTCMinutes());

    return freeze ? `${hour}:${minute}` : `${utcHour}:${utcMinute}`;
  }
  return "";
}

export function convertToTwoDigits(num: number | undefined): string {
  if (!num) return "00";
  if (num.toString()?.length >= 3) {
    return num?.toString();
  }
  return `00${num}`.slice(-2);
}

export const validateDate = (date: string | Date) => {
  if (!date) return false;
  const newDate = new Date(date);

  return !Number.isNaN(newDate.getTime());
};

// Please don't remove this comments , maybe we need later

// export const validateTime = (time: string | Date): boolean => {
//   if (!time) return false;

//   let timeString: string;

//   // If the time is a Date object, convert it to a string in HH:mm:ss format
//   if (time instanceof Date) {
//     // Extract time from the Date object and convert it to HH:mm:ss format
//     const hours = time.getHours().toString().padStart(2, "0");
//     const minutes = time.getMinutes().toString().padStart(2, "0");
//     const seconds = time.getSeconds().toString().padStart(2, "0");

//     timeString = `${hours}:${minutes}:${seconds}`;
//   } else {
//     // If it's already a string, just assign it
//     timeString = time;
//   }

//   // Regular expression to match HH:mm:ss format
//   const timeRegex = /^([01]?\d|2[0-3]):([0-5]?\d):([0-5]?\d)$/;
//   const match = timeString.match(timeRegex);

//   return match !== null;
// };

interface ITimerState {
  time: string;
  isLessThanOneMinute: boolean;
  isTimerFinished: boolean;
}

// Custom hook for timer calculation
export const useTimer = (endDate: Date | string): ITimerState => {
  const end = new Date(endDate);
  const isInvalidDate = Number.isNaN(end.getTime());

  const calculateTimeLeft = () => {
    const diff = end.getTime() - Date.now();

    if (diff <= 0) {
      return {
        time: "00:00:00",
        isLessThanOneMinute: false,
        isTimerFinished: true,
      };
    }

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return {
      time: `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`,
      isLessThanOneMinute: diff <= 60_000,
      isTimerFinished: false,
    };
  };

  const [timerState, setTimerState] = useState<ITimerState>(
    isInvalidDate
      ? { time: "--:--:--", isLessThanOneMinute: false, isTimerFinished: false }
      : calculateTimeLeft(),
  );

  useEffect(() => {
    if (isInvalidDate) return;

    setTimerState(calculateTimeLeft());

    const intervalId = setInterval(() => {
      setTimerState((prev) => {
        const newState = calculateTimeLeft();

        return JSON.stringify(prev) === JSON.stringify(newState)
          ? prev
          : newState;
      });
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endDate]);

  return timerState;
};

export function useConvertDate() {
  const locale = useUiStore(useShallow((state) => state.locale));

  const stringifyDate = useCallback(
    (date: string | Date): string => {
      if (validateDate(date)) {
        return new Date(date).toLocaleString(locale, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });
      }
      return "- - -";
    },
    [locale],
  );

  const stringifyFullDate = useCallback(
    (date: string | Date): string => {
      if (validateDate(date)) {
        return new Date(date).toLocaleString(locale, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      return "- - -";
    },
    [locale],
  );

  const humanizeDate = useCallback(
    (date: string | Date): string => {
      if (validateDate(date)) {
        return new Date(date).toLocaleString(locale, {
          year: "numeric",
          month: "long",
          day: "2-digit",
        });
      }
      return "- - -";
    },
    [locale],
  );

  const humanizeTime = useCallback(
    (date: string | Date): string => {
      if (validateDate(date)) {
        return new Date(date).toLocaleString(locale, {
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      return "- - -";
    },
    [locale],
  );

  const humanizeFullTime = useCallback(
    (date: string | Date): string => {
      if (validateDate(date)) {
        return new Date(date).toLocaleString(locale, {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
      }
      return "- - -";
    },
    [locale],
  );

  const humanizeFullDate = useCallback(
    (date: string | Date): string => {
      if (validateDate(date)) {
        return new Date(date).toLocaleString(locale, {
          year: "numeric",
          month: "long",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      return "- - -";
    },
    [locale],
  );

  function getDayName(date: string | Date) {
    return new Intl.DateTimeFormat(locale, { weekday: "long" }).format(
      new Date(date),
    );
  }

  return {
    humanizeDate,
    humanizeTime,
    humanizeFullTime,
    humanizeFullDate,
    getDayName,
    stringifyDate,
    stringifyFullDate,
    formatDateToPersian,
  };
}

function formatDateToPersian(date: string | Date): string {
  if (validateDate(date)) {
    const subFormattedDate = new Date(date);

    if (Number.isNaN(subFormattedDate.getTime())) {
      throw new TypeError("Invalid date format");
    }

    const persianDate = subFormattedDate.toLocaleDateString("fa-IR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      calendar: "persian",
    });

    const time = subFormattedDate.toLocaleTimeString("fa-IR", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      // Use 24-hour format
      hour12: false,
    });

    return `${time} - ${persianDate}`;
  }
  return "- - -";
}

export function getTwoDigitDuration(
  duration: number | string | undefined,
): string {
  if (!duration) return "00:00";
  const durationSeconds = Number(duration || 0);

  const hours = Math.floor(durationSeconds / 3600);
  const minutes = Math.floor((durationSeconds % 3600) / 60);
  const seconds = durationSeconds % 60;

  return hours > 0
    ? `${convertToTwoDigits(hours)}:${convertToTwoDigits(minutes)}:${convertToTwoDigits(seconds)}`
    : `${convertToTwoDigits(minutes)}:${convertToTwoDigits(seconds)}`;
}

function formatRemainingDuration(
  durationInSeconds: number,
  locale = "fa",
): string {
  // If duration is less than one second, format it as seconds with fractional digits.
  if (durationInSeconds < 1) {
    return "0";
  }

  // Define the time units in descending order of magnitude.
  const units: { unit: Intl.NumberFormatOptions["unit"]; ms: number }[] = [
    { unit: "day", ms: 24 * 60 * 60 * 1000 },
    { unit: "hour", ms: 60 * 60 * 1000 },
    { unit: "minute", ms: 60 * 1000 },
    { unit: "second", ms: 1000 },
  ];

  // Pick the largest unit that fits into the duration.
  for (const { unit, ms } of units) {
    if (durationInSeconds * 1000 >= ms) {
      // Calculate the value in that unit and round to the nearest integer.
      const value = Math.floor((durationInSeconds * 1000) / ms);

      return new Intl.NumberFormat(locale, {
        style: "unit",
        unit,
        unitDisplay: "long",
      }).format(value);
    }
  }

  // This line should never be reached.
  return "";
}

function formatDuration(durationInSeconds: number, locale = "fa"): string {
  // For durations under one minute, show seconds only.
  if (durationInSeconds < 60) {
    // Round to the nearest whole second.
    const seconds = Math.round(durationInSeconds);

    return new Intl.NumberFormat(locale, {
      style: "unit",
      unit: "second",
      unitDisplay: "long",
      maximumFractionDigits: 0,
    }).format(seconds);
  }

  // For durations less than one hour, show minutes only.
  if (durationInSeconds < 3600) {
    // Convert seconds to minutes and round.
    const minutes = Math.round(durationInSeconds / 60);

    return new Intl.NumberFormat(locale, {
      style: "unit",
      unit: "minute",
      unitDisplay: "long",
      maximumFractionDigits: 0,
    }).format(minutes);
  }

  // For durations of one hour or more, display hours and minutes.
  // First, compute the full hours.
  const hours = Math.floor(durationInSeconds / 3600);
  // Compute the remaining seconds after removing the full hours.
  const remainingSeconds = durationInSeconds - hours * 3600;
  // Convert the remainder to minutes (floor to nearest integer).
  const minutes = Math.floor(remainingSeconds / 60);

  // In case the minutes round up to 60, adjust accordingly.
  let finalHours = hours;
  let finalMinutes = minutes;

  if (finalMinutes === 60) {
    finalHours++;
    finalMinutes = 0;
  }

  // Format the hours.
  const formattedHours = new Intl.NumberFormat(locale, {
    style: "unit",
    unit: "hour",
    unitDisplay: "long",
    maximumFractionDigits: 0,
  }).format(finalHours);

  // If there are remaining minutes, format them and combine.
  if (finalMinutes > 0) {
    const formattedMinutes = new Intl.NumberFormat(locale, {
      style: "unit",
      unit: "minute",
      unitDisplay: "long",
      maximumFractionDigits: 0,
    }).format(finalMinutes);

    // Combine the hour and minute parts.
    return `${formattedHours} ${formattedMinutes}`;
  }

  // If there are no remaining minutes, return just the hours.
  return formattedHours;
}

/**
 * React hook that memoizes the formatted duration.
 *
 * @param duration - Duration in milliseconds.
 * @returns A memoized localized duration string.
 */
export function useFormattedDuration(
  duration: number | string | undefined,
): string {
  const locale = useUiStore(useShallow((state) => state.locale));

  return useMemo(
    () => formatDuration(Number(duration || 0) || 0, locale),
    [duration, locale],
  );
}

export const getTomorrow = (days = 1) => addDays(new Date(), days);
export const getNextMonth = (nextMonth = 1) => addMonths(new Date(), nextMonth);

export function combineDates(date1: Date, date2: Date): Date {
  // Extract year, month, and day from date1
  const year = date1.getFullYear();
  const month = date1.getMonth();
  const day = date1.getDate();

  // Extract hours, minutes, seconds, and milliseconds from date2
  const hours = date2.getHours();
  const minutes = date2.getMinutes();

  // Create a new Date by combining the extracted parts
  return new Date(year, month, day, hours, minutes);
}
/**
 *
 * @param value it is used in fast date picker. when the data is in Y-M-D mode convert it to new Date() then the date will show in shamsi mode
 * @returns
 */
export function convertToDate(value: Date | string) {
  if (!!value && validateDate(value)) {
    return new Date(value);
  }
}
/**
 * if your date and time are separate and want to merge them as a unit date time,
 *  use this function
 * @param date date or string
 * @param time date or string
 * @returns date time
 */
export function mergeDateAndTime(date: string | Date, time: string | Date) {
  const mergedDate = new Date(date);
  const timeDate = new Date(time);

  mergedDate.setHours(timeDate.getHours(), timeDate.getMinutes());

  return mergedDate;
}

export function isPastDateTime(dateTime: Date | string) {
  return Date.now() > new Date(dateTime).getTime();
}

/**
 * Calculates the difference in days between two dates.
 * @param date1 - The first date (string or Date)
 * @param date2 - The second date (string or Date), defaults to today
 * @returns The number of days (can be negative if date1 < date2)
 */
export const getDaysBetween = (
  date1: string | Date,
  date2: string | Date = new Date(),
): number => {
  // Validate both dates
  if (validateDate(date1) || validateDate(date2)) {
    const d1 = new Date(date1);
    const d2 = new Date(date2);

    // Calculate difference in milliseconds
    const diffInMs = d1.getTime() - d2.getTime();

    // Convert milliseconds to full days (keep sign)
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    // Return rounded days (you can use Math.floor if you want to ignore partial days)
    return Math.round(diffInDays);
  }

  return 0;
};

export function useCalculateDuration(start: string | Date, end: string | Date) {
  const { t } = useTranslation("component");
  const locale = useUiStore(useShallow((state) => state.locale));
  const startDate = useMemo(
    () => (start instanceof Date ? start : new Date(start)),
    [start],
  );
  const endDate = useMemo(
    () => (end instanceof Date ? end : new Date(end)),
    [end],
  );

  const getRemainingTime = useCallback((): string => {
    const now = new Date();
    const remainingMs = endDate.getTime() - now.getTime();

    if (remainingMs <= 0) {
      return t("courseDetailBox.endTimeDescription");
    }

    // Calculate the remaining time components.
    const days = Math.floor(remainingMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (remainingMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
    );
    const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((remainingMs % (1000 * 60)) / 1000);

    // Create dedicated formatters for each time unit using style "unit".
    // Note: For hours, minutes, and seconds we enforce two-digit formatting.
    const dayFormatter = new Intl.NumberFormat(locale, {
      style: "unit",
      unit: "day",
      unitDisplay: "long",
      minimumIntegerDigits: 1,
      useGrouping: false,
    });
    const hourFormatter = new Intl.NumberFormat(locale, {
      style: "unit",
      unit: "hour",
      unitDisplay: "long",
      minimumIntegerDigits: 2,
      useGrouping: false,
    });

    const minuteFormatter = new Intl.NumberFormat(locale, {
      style: "unit",
      unit: "minute",
      unitDisplay: "long",
      minimumIntegerDigits: 2,
      useGrouping: false,
    });

    const secondFormatter = new Intl.NumberFormat(locale, {
      style: "unit",
      unit: "second",
      unitDisplay: "long",
      minimumIntegerDigits: 2,
      useGrouping: false,
    });

    // Build the output parts, including only nonzero units except for seconds.
    const parts: string[] = [];

    if (days > 0) {
      parts.push(dayFormatter.format(days));
    }
    if (hours > 0) {
      parts.push(hourFormatter.format(hours));
    }
    if (minutes > 0) {
      parts.push(minuteFormatter.format(minutes));
    }
    // Always include seconds.
    parts.push(secondFormatter.format(seconds));

    return parts.join(" ");
  }, [t, endDate, locale]);

  const getRemainingDays = useCallback((): string => {
    const now = new Date();
    const remainingMs = endDate.getTime() - now.getTime();

    if (remainingMs < 1000) {
      return t("courseDetailBox.endTimeDescription");
    }

    const remainingDays = Math.floor(remainingMs / (1000 * 60 * 60 * 24));

    return `${remainingDays}`;
  }, [t, endDate]);

  function getRemainingDuration() {
    const remainingMs = endDate.getTime() - startDate.getTime();

    return formatRemainingDuration(remainingMs / 1000, locale);
  }

  return {
    getRemainingTime,
    getRemainingDays,
    getRemainingDuration,
  };
}
